// Smoke Drive details pane — preview, activity, share, print, Open

import React from 'react';
import {
    CloseOutlined,
    CopyOutlined,
    FileOutlined,
    FolderOutlined,
    PrinterOutlined,
    BarcodeOutlined,
} from '@ant-design/icons';
import {
    DRIVE_ITEM_TYPE,
    DRIVE_SCOPE,
    formatDriveModified,
} from '../../services/operatorDriveService';
import { formatOperatorDriveBytes } from '../../services/operatorDriveFileService';
import { isOperatorDriveItemPrintable } from '../../utils/operatorDrivePrint';
import { isShippingLabelPdfDriveItem } from '../../utils/shippingLabelPdfHandoff';
import OperatorDriveShareControls from './OperatorDriveShareControls';
import OperatorDrivePreview from './OperatorDrivePreview';

function ownerLabel(item, uid) {
    if (item.scope === DRIVE_SCOPE.SHARED) return 'Shared team';
    if (item.ownerUid === uid || item.createdBy === uid) return 'me';
    return item.ownerUid || '—';
}

/**
 * @param {{
 *   item: object | null,
 *   uid: string,
 *   userData: object,
 *   library: object[],
 *   printSizeKey: string,
 *   printingId: string | null,
 *   matchingId?: string | null,
 *   onClose: () => void,
 *   onPrint: (item: object) => void,
 *   onMatchShippingLabels?: (item: object) => void,
 *   onStatus: (msg: string) => void,
 *   onError: (msg: string) => void,
 *   onDuplicate: (item: object) => void,
 *   onZip: (item: object) => void,
 * }} props
 */
export default function OperatorDriveDetailsPane({
    item,
    uid,
    userData,
    library,
    printSizeKey,
    printingId,
    matchingId = null,
    onClose,
    onPrint,
    onMatchShippingLabels,
    onStatus,
    onError,
    onDuplicate,
    onZip,
}) {
    if (!item) {
        return (
            <aside className="op-drive__details" aria-label="File details">
                <div className="op-drive__details-empty">
                    <p>Select a file or folder to view details.</p>
                    <p className="op-drive__details-empty-hint">
                        Preview, shares, download activity, and print size appear here.
                    </p>
                </div>
            </aside>
        );
    }

    const isFolder = item.type === DRIVE_ITEM_TYPE.FOLDER;
    const canAct = !item.trashed;

    return (
        <aside className="op-drive__details" aria-label="File details">
            <div className="op-drive__details-head">
                <div className="op-drive__details-icon" aria-hidden="true">
                    {isFolder ? <FolderOutlined /> : <FileOutlined />}
                </div>
                <button
                    type="button"
                    className="op-msg-tool-btn op-drive__details-close"
                    aria-label="Close details"
                    onClick={onClose}
                >
                    <CloseOutlined />
                </button>
            </div>
            <h3 className="op-drive__details-name" title={item.name}>
                {item.name}
            </h3>
            {!isFolder && canAct ? <OperatorDrivePreview item={item} /> : null}
            <p className="op-drive__details-kicker">Details</p>
            <dl className="op-drive__details-dl">
                <div>
                    <dt>Kind</dt>
                    <dd>{isFolder ? 'Folder' : 'File'}</dd>
                </div>
                {!isFolder ? (
                    <div>
                        <dt>Type</dt>
                        <dd>{item.contentType || 'Unknown'}</dd>
                    </div>
                ) : null}
                {item.originalFileName && item.originalFileName !== item.name ? (
                    <div>
                        <dt>Original name</dt>
                        <dd>{item.originalFileName}</dd>
                    </div>
                ) : null}
                <div>
                    <dt>Size</dt>
                    <dd>{isFolder ? '—' : formatOperatorDriveBytes(item.sizeBytes)}</dd>
                </div>
                <div>
                    <dt>Owner</dt>
                    <dd>{ownerLabel(item, uid)}</dd>
                </div>
                <div>
                    <dt>Uploaded</dt>
                    <dd>{formatDriveModified(item.createdAt)}</dd>
                </div>
                <div>
                    <dt>Last modified</dt>
                    <dd>{formatDriveModified(item.updatedAt || item.createdAt)}</dd>
                </div>
                {!isFolder && item.printSizeKey ? (
                    <div>
                        <dt>Print size</dt>
                        <dd>{item.printSizeKey}</dd>
                    </div>
                ) : null}
                <div>
                    <dt>Starred</dt>
                    <dd>{item.starred ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                    <dt>Location</dt>
                    <dd>
                        {item.scope === DRIVE_SCOPE.SHARED ? 'Shared team' : 'My files'}
                        {item.trashed ? ' · Trash' : ''}
                    </dd>
                </div>
                {item.trashed ? (
                    <div>
                        <dt>Trashed</dt>
                        <dd>{formatDriveModified(item.trashedAt)}</dd>
                    </div>
                ) : null}
            </dl>
            {canAct ? (
                <div className="op-drive__details-actions">
                    {!isFolder && isOperatorDriveItemPrintable(item) ? (
                        <button
                            type="button"
                            className="op-msg-btn op-msg-btn--primary op-msg-btn--compact"
                            disabled={printingId === item.id}
                            onClick={() => onPrint(item)}
                            title={`Add to mixed print queue on ${printSizeKey}`}
                        >
                            <PrinterOutlined />{' '}
                            {printingId === item.id ? 'Queuing…' : `Print (${printSizeKey})`}
                        </button>
                    ) : null}
                    {!isFolder &&
                    isShippingLabelPdfDriveItem(item) &&
                    typeof onMatchShippingLabels === 'function' ? (
                        <button
                            type="button"
                            className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                            disabled={matchingId === item.id}
                            onClick={() => onMatchShippingLabels(item)}
                            title="Open Printing Toolkit Labels with this PDF file for order matching"
                        >
                            <BarcodeOutlined />{' '}
                            {matchingId === item.id ? 'Opening…' : 'Match to orders'}
                        </button>
                    ) : null}
                    {!isFolder ? (
                        <button
                            type="button"
                            className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                            onClick={() => onDuplicate(item)}
                        >
                            Duplicate
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                        onClick={() => onZip(item)}
                    >
                        {isFolder ? 'Download zip' : 'Download'}
                    </button>
                    <OperatorDriveShareControls
                        item={item}
                        uid={uid}
                        userData={userData}
                        library={library}
                        onStatus={onStatus}
                        onError={onError}
                        variant="details"
                    />
                    {!isFolder && item.downloadUrl ? (
                        <a
                            className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                            href={item.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <CopyOutlined /> Open
                        </a>
                    ) : null}
                </div>
            ) : null}
        </aside>
    );
}
